import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { submitOrderToSheet } from "../services/submitOrderToSheet";
import { getLoyaltyStatus, validateCouponCode } from "../services/orderPortal";
import {
  buildItemsSummary,
  getCartTotals,
  getOrderTotals,
  isValidEmail,
} from "../utils/checkoutOrder";
import { computeParcelFromCart } from "../utils/computeParcelFromCart";
import { fetchCheckoutShippingRates } from "../services/shiprocketRates";
import { formatInrAmount } from "../utils/formatInrAmount";
import { getCustomerSession, sessionMatchesEmail } from "../utils/customerSession";
import { giftLidPositionLabel } from "../components/GiftBoxLidEditor";
import { useDeliveryHints } from "../hooks/useDeliveryHints";
import TrustBadges from "../components/TrustBadges";
import { BtnContent } from "../components/BtnIcon";
import {
  IoArrowBackOutline,
  IoCardOutline,
  IoCashOutline,
  IoCheckmarkCircle,
  IoLinkOutline,
  IoRefreshOutline,
  IoTicketOutline,
  IoCloseOutline,
  IoLocationOutline,
} from "react-icons/io5";
import {
  isSmepayCheckoutEnabled,
  loadSmepayWizardScript,
  smepayPollStatus,
  smepayStartPayment,
  smepayValidatePayment,
} from "../services/smepayClient";

const initialCustomer = {
  name: "",
  email: "",
  phone: "",
  address: "",
  pincode: "",
};

const INTENT_ICONS = {
  gpay: "https://typof.co/gpay.png",
  phonepe: "https://typof.co/phonepe.png",
  paytm: "https://typof.co/paytm.png",
  bhim: "https://typof.co/bhim.png",
};

function Checkout({ cart, clearCart }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(() => {
    const fromCart = location.state?.customer;
    if (fromCart && typeof fromCart === "object") {
      return {
        name: fromCart.name ?? "",
        email: fromCart.email ?? "",
        phone: fromCart.phone ?? "",
        address: fromCart.address ?? "",
        pincode: fromCart.pincode ?? "",
      };
    }
    return initialCustomer;
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [sheetMessage, setSheetMessage] = useState(null);
  const [sheetSubmitting, setSheetSubmitting] = useState(false);
  const LOYALTY_VOUCHER_INR = 750;
  const smepayEnabled = isSmepayCheckoutEnabled();
  const [paymentMethod, setPaymentMethod] = useState(() => (smepayEnabled ? "prepaid" : "cod"));
  const isCod = paymentMethod === "cod";
  const parcelPaymentMethod = isCod ? "COD" : "Prepaid";

  const [voucherEligible, setVoucherEligible] = useState(false);
  const [useLoyaltyVoucher, setUseLoyaltyVoucher] = useState(false);

  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [couponApplying, setCouponApplying] = useState(false);

  const [smepaySession, setSmepaySession] = useState(null);
  const pollRef = useRef(null);
  const ratesRequestRef = useRef(0);

  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState(null);
  const [ratesMeta, setRatesMeta] = useState(null);

  const { hints, hasSuggestions, sessionEmail } = useDeliveryHints(
    cart.length,
    setCustomer,
  );

  useEffect(() => {
    const fromCart = location.state?.customer;
    if (fromCart && typeof fromCart === "object") {
      setCustomer({
        name: fromCart.name ?? "",
        email: fromCart.email ?? "",
        phone: fromCart.phone ?? "",
        address: fromCart.address ?? "",
        pincode: fromCart.pincode ?? "",
      });
    }
  }, [location.state]);

  useEffect(() => {
    let cancelled = false;
    if (!sessionMatchesEmail(customer.email)) {
      setVoucherEligible(false);
      setUseLoyaltyVoucher(false);
      return () => {
        cancelled = true;
      };
    }
    const sess = getCustomerSession();
    if (!sess?.token) {
      setVoucherEligible(false);
      setUseLoyaltyVoucher(false);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      const res = await getLoyaltyStatus(customer.email, sess.token);
      if (cancelled || !res.ok || !res.data) return;
      const eligible =
        !!res.data.nextLoyaltyVoucher || !!res.data.nextOrderIsFree;
      setVoucherEligible(eligible);
      if (!eligible) setUseLoyaltyVoucher(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [customer.email]);

  const cartSig = cart
    .map((i) => {
      const lay = i.giftLidLayout ? JSON.stringify(i.giftLidLayout) : "";
      return `${i.id}:${i.quantity}:${i.price}:${i.giftLidMessage || ""}:${lay}:${i.giftLidTextColor || ""}`;
    })
    .join("|");

  useEffect(() => {
    setAppliedCoupon(null);
    setCouponError(null);
    setSmepaySession(null);
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [cartSig, useLoyaltyVoucher]);

  useEffect(() => {
    if (cart.length === 0) {
      navigate("/cart", { replace: true });
    }
  }, [cart.length, navigate]);

  useEffect(() => {
    if (cart.length === 0) return;
    const em = String(customer.email || "").trim();
    if (!em || !sessionMatchesEmail(em)) {
      navigate("/checkout-verify", {
        replace: true,
        state: {
          customer: {
            name: customer.name,
            email: em,
            phone: customer.phone,
            address: customer.address,
            pincode: customer.pincode,
          },
        },
      });
    }
  }, [
    cart.length,
    customer.email,
    customer.name,
    customer.phone,
    customer.address,
    customer.pincode,
    navigate,
  ]);

  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!smepayEnabled) {
      setPaymentMethod("cod");
    }
  }, [smepayEnabled]);

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setSmepaySession(null);
    if (isCod) {
      setSheetMessage(null);
    }
  }, [paymentMethod, isCod]);

  const pincode = customer.pincode.trim();

  useEffect(() => {
    if (!/^\d{6}$/.test(pincode)) {
      ratesRequestRef.current += 1;
      setShippingOptions([]);
      setSelectedShipping(null);
      setRatesError(null);
      setRatesMeta(null);
      setRatesLoading(false);
      return undefined;
    }

    const requestId = ++ratesRequestRef.current;
    const timer = window.setTimeout(() => {
      setRatesLoading(true);
      setRatesError(null);
      setShippingOptions([]);
      setSelectedShipping(null);
      setRatesMeta(null);

      (async () => {
        try {
          const parcel = computeParcelFromCart(cart, parcelPaymentMethod);
          const res = await fetchCheckoutShippingRates({ pincode, parcel });
          if (requestId !== ratesRequestRef.current) return;

          if (!res.ok) {
            setRatesError(res.message || "Could not load shipping rates.");
            setShippingOptions([]);
            setSelectedShipping(null);
            return;
          }

          const options = (res.options || [])
            .map((option) => ({
              ...option,
              rate: Number(option.rate),
            }))
            .filter((option) => Number.isFinite(option.rate) && option.rate >= 0)
            .sort((a, b) => a.rate - b.rate);

          setRatesMeta({
            senderPincode: res.senderPincode,
            receiverPincode: res.receiverPincode,
          });
          setShippingOptions(options);

          if (options.length === 0) {
            setRatesError("No shipping options are available for this pincode.");
            setSelectedShipping(null);
            return;
          }

          setRatesError(null);
          setSelectedShipping(options[0]);
        } catch (err) {
          if (requestId !== ratesRequestRef.current) return;
          setRatesError(err?.message || "Could not load shipping rates.");
          setShippingOptions([]);
          setSelectedShipping(null);
        } finally {
          if (requestId === ratesRequestRef.current) {
            setRatesLoading(false);
          }
        }
      })();
    }, 400);

    return () => {
      window.clearTimeout(timer);
    };
  }, [pincode, cartSig, cart, parcelPaymentMethod]);

  const { subtotal } = getCartTotals(cart);
  const itemsSummary = buildItemsSummary(cart);

  const voucherDiscount =
    useLoyaltyVoucher && voucherEligible
      ? Math.min(LOYALTY_VOUCHER_INR, subtotal)
      : 0;
  const couponDiscount = appliedCoupon ? Number(appliedCoupon.discountAmount) || 0 : 0;
  const shippingCost = selectedShipping ? Math.max(0, Number(selectedShipping.rate) || 0) : 0;
  const { merchandiseTotal, orderPayableTotal } = getOrderTotals({
    cart,
    voucherDiscount,
    couponDiscount,
    shippingCost,
  });

  const validateCustomer = () => {
    const err = {};
    if (!customer.name.trim()) err.name = "Name is required";
    if (!customer.email.trim()) err.email = "Email is required";
    else if (!isValidEmail(customer.email)) err.email = "Enter a valid email address";
    if (!customer.phone.trim()) err.phone = "Phone is required";
    else if ((customer.phone.match(/\d/g) || []).length < 10)
      err.phone = "Enter a valid phone number (at least 10 digits)";
    if (!customer.address.trim()) err.address = "Address is required";
    if (!customer.pincode.trim()) err.pincode = "Pincode is required";
    else if (!/^\d{6}$/.test(customer.pincode.trim()))
      err.pincode = "Use a 6-digit pincode";
    setFieldErrors(err);
    return Object.keys(err).length === 0;
  };

  const getShippingValidationMessage = () => {
    if (!/^\d{6}$/.test(customer.pincode.trim())) {
      return "Enter a valid 6-digit pincode to see shipping options.";
    }
    if (ratesLoading) return "Loading shipping options…";
    if (shippingOptions.length === 0) {
      return ratesError || "Shipping is not available for this pincode.";
    }
    if (!selectedShipping) return "Select a shipping option.";
    return null;
  };

  const validateShippingSelection = () => {
    const message = getShippingValidationMessage();
    if (message) {
      setSheetMessage(message);
      return false;
    }
    return true;
  };

  const onCustomerChange = (field, value) => {
    setCustomer((c) => ({ ...c, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((e) => ({ ...e, [field]: undefined }));
    }
  };

  const itemsDetailForApi = cart.map((item) => ({
    id: item.id,
    name: item.name,
    quantity: item.quantity,
    price: item.price,
    ...(item.giftLidMessage ? { giftLidMessage: item.giftLidMessage } : {}),
    ...(item.giftLidLayout ? { giftLidLayout: item.giftLidLayout } : {}),
    ...(item.giftLidTextColor ? { giftLidTextColor: item.giftLidTextColor } : {}),
  }));

  const handleApplyCoupon = async () => {
    setCouponError(null);
    const raw = couponInput.trim();
    if (!raw) {
      setCouponError("Enter a coupon code.");
      setAppliedCoupon(null);
      return;
    }
    const applyVoucher =
      useLoyaltyVoucher &&
      voucherEligible &&
      sessionMatchesEmail(customer.email) &&
      getCustomerSession()?.token;

    setCouponApplying(true);
    const res = await validateCouponCode({
      couponCode: raw,
      email: customer.email.trim(),
      itemsDetail: itemsDetailForApi,
      loyaltyVoucherOrder: Boolean(applyVoucher),
      loyaltySessionToken: applyVoucher ? getCustomerSession().token : "",
    });
    setCouponApplying(false);

    if (!res.ok) {
      setCouponError(res.message || "Could not validate coupon.");
      setAppliedCoupon(null);
      return;
    }
    const d = res.data;
    setAppliedCoupon({
      code: d.couponCode || raw,
      discountAmount: d.discountAmount,
      discountType: d.discountType,
      finalTotal: d.finalTotal,
    });
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponError(null);
  };

  const buildOrderPayload = (orderId, sess) => {
    const payload = {
      orderId,
      customerName: customer.name.trim(),
      email: customer.email.trim(),
      phone: customer.phone.trim(),
      address: customer.address.trim(),
      pincode: customer.pincode.trim(),
      itemsSummary,
      itemsDetail: itemsDetailForApi,
      subtotal,
      shipping: shippingCost,
      total: orderPayableTotal,
      shippingCourierId: selectedShipping?.courierCompanyId || "",
      shippingCourierName: selectedShipping?.courierName || "",
      shippingEtd: selectedShipping?.etd || "",
      paymentMethod: parcelPaymentMethod,
      checkoutSessionToken: sess.token,
    };

    const applyVoucher =
      useLoyaltyVoucher &&
      voucherEligible &&
      sessionMatchesEmail(customer.email) &&
      getCustomerSession()?.token;
    if (applyVoucher) {
      payload.loyaltyVoucherOrder = true;
      payload.loyaltySessionToken = getCustomerSession().token;
    }

    if (appliedCoupon?.code) {
      payload.couponCode = appliedCoupon.code;
    }

    return payload;
  };

  const finalizeOrder = async (payload) => {
    const sheetResult = await submitOrderToSheet(payload);
    setSheetSubmitting(false);

    if (!sheetResult.ok) {
      setSheetMessage(
        sheetResult.message ||
          "Could not save the order. Check your connection and try again.",
      );
      return;
    }

    const placedOrderId = sheetResult.data?.orderId || payload.orderId;
    const warn = sheetResult.warning;

    clearCart();
    navigate("/orders", {
      replace: true,
      state: {
        orderPlaced: true,
        orderId: placedOrderId,
        orderWarning: warn || null,
        guestEmail:
          getCustomerSession()?.token && sessionMatchesEmail(customer.email)
            ? null
            : customer.email.trim(),
      },
    });
  };

  const placeOrderAfterPayment = async (smepayMeta, paymentStatusLabel) => {
    const orderId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID().slice(0, 8)
        : String(Date.now()).slice(-8);

    const sess = getCustomerSession();
    if (!sess?.token) {
      setSheetSubmitting(false);
      setSheetMessage("Session expired. Verify your email again from the cart.");
      return;
    }

    const payload = buildOrderPayload(orderId, sess);
    payload.requireSmepayPayment = true;
    payload.paymentMethod = "Prepaid";
    payload.paymentVerified = true;
    payload.smepayOrderId = smepayMeta.smepayOrderId;
    payload.smepaySlug = smepayMeta.slug;
    payload.paymentStatus = paymentStatusLabel || "SUCCESS";

    await finalizeOrder(payload);
  };

  const tryValidateAndSubmit = async (smepayMeta) => {
    const amountNum = Number(smepayMeta.amount);
    const { data } = await smepayValidatePayment(smepayMeta.slug, amountNum);
    if (data?.paid) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      setSheetSubmitting(true);
      await placeOrderAfterPayment(smepayMeta, data.payment_status || "SUCCESS");
      return true;
    }
    return false;
  };

  const startStatusPolling = (meta) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let ticks = 0;
    const maxTicks = 72;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > maxTicks) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        return;
      }
      const { data } = await smepayPollStatus(meta.smepayOrderId);
      if (data?.paid) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setSheetSubmitting(true);
        const ok = await tryValidateAndSubmit(meta);
        if (!ok) {
          setSheetSubmitting(false);
          setSheetMessage(
            "Payment looks received but confirmation failed. Tap “Check payment” or contact us with your order details.",
          );
        }
      }
    }, 5000);
  };

  const beginSmepayInline = async (startData) => {
    const meta = {
      slug: startData.slug,
      smepayOrderId: startData.smepayOrderId,
      amount: startData.amount,
      qr_code: startData.qr_code || "",
      payment_link: startData.payment_link || "",
      intents: startData.intents || {},
      displayMode: "inline",
    };
    setSmepaySession(meta);
    setSheetMessage(
      "Pay with UPI using the QR or an app link below. We confirm payment automatically, or use Check payment.",
    );
    startStatusPolling(meta);
  };

  const beginSmepayWizard = async (startData) => {
    await loadSmepayWizardScript();
    if (typeof window.smepayCheckout !== "function") {
      setSheetMessage("Could not load SMEPay checkout. Refresh and try again.");
      setSheetSubmitting(false);
      return;
    }
    const meta = {
      slug: startData.slug,
      smepayOrderId: startData.smepayOrderId,
      amount: startData.amount,
      displayMode: "wizard",
    };
    setSmepaySession(meta);
    window.smepayCheckout({
      slug: startData.slug,
      onSuccess: async () => {
        setSheetSubmitting(true);
        const ok = await tryValidateAndSubmit(meta);
        if (!ok) {
          setSheetSubmitting(false);
          setSheetMessage(
            "We could not confirm payment yet. Use Check payment or try again.",
          );
        }
      },
      onFailure: () => {
        setSheetMessage("Payment window closed. Tap Pay with UPI to try again.");
        setSheetSubmitting(false);
      },
    });
    setSheetSubmitting(false);
  };

  const handlePayWithUPI = async () => {
    setSheetMessage(null);
    if (!validateCustomer()) {
      setSheetMessage(
        "Please complete all delivery fields (red errors above) before paying.",
      );
      return;
    }
    if (!validateShippingSelection()) return;
    const sess = getCustomerSession();
    if (!sess?.token || !sessionMatchesEmail(customer.email)) {
      setSheetMessage(
        "Your email is not verified. Go back to the cart and enter the code we sent you.",
      );
      return;
    }
    if (orderPayableTotal < 1) {
      setSheetMessage(`Order total must be at least ₹${formatInrAmount(1)} to pay online.`);
      return;
    }

    setSheetSubmitting(true);
    const orderId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID().slice(0, 8)
        : String(Date.now()).slice(-8);

    const { res, data } = await smepayStartPayment({
      amount: orderPayableTotal,
      internalOrderId: orderId,
      customer: {
        name: customer.name.trim(),
        email: customer.email.trim(),
        phone: customer.phone.trim(),
      },
    });

    if (!res.ok || !data?.ok) {
      setSheetSubmitting(false);
      setSheetMessage(
        data?.message ||
          `Could not start SMEPay (${res.status}). Check server configuration.`,
      );
      return;
    }

    if (data.displayMode === "wizard") {
      await beginSmepayWizard(data);
      return;
    }

    await beginSmepayInline(data);
    setSheetSubmitting(false);
  };

  const handleCheckPayment = async () => {
    if (!smepaySession) return;
    setSheetMessage(null);
    setSheetSubmitting(true);
    const ok = await tryValidateAndSubmit(smepaySession);
    setSheetSubmitting(false);
    if (!ok) {
      setSheetMessage(
        "Payment not confirmed yet. Complete UPI payment, wait a few seconds, and tap Check payment again.",
      );
    }
  };

  const placeOrderWithCod = async () => {
    setSheetMessage(null);
    if (!validateCustomer()) {
      setSheetMessage(
        "Please complete all delivery fields (red errors above) before placing your order.",
      );
      return;
    }
    if (!validateShippingSelection()) return;
    const sess = getCustomerSession();
    if (!sess?.token || !sessionMatchesEmail(customer.email)) {
      setSheetMessage(
        "Your email is not verified. Go back to the cart and enter the code we sent you.",
      );
      return;
    }

    setSheetSubmitting(true);
    const orderId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID().slice(0, 8)
        : String(Date.now()).slice(-8);

    const payload = buildOrderPayload(orderId, sess);
    payload.paymentMethod = "COD";
    payload.paymentStatus = "COD";
    payload.paymentVerified = false;
    await finalizeOrder(payload);
  };

  const saveOrderToSheet = async () => {
    setSheetMessage(null);
    if (!validateCustomer()) {
      setSheetMessage(
        "Please complete all delivery fields (red errors above) before saving.",
      );
      return;
    }
    if (!validateShippingSelection()) return;
    const sess = getCustomerSession();
    if (!sess?.token || !sessionMatchesEmail(customer.email)) {
      setSheetMessage(
        "Your email is not verified. Go back to the cart and enter the code we sent you.",
      );
      return;
    }

    setSheetSubmitting(true);
    const orderId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID().slice(0, 8)
        : String(Date.now()).slice(-8);

    const payload = buildOrderPayload(orderId, sess);
    await finalizeOrder(payload);
  };

  const handlePrimaryClick = () => {
    if (isCod) {
      placeOrderWithCod();
      return;
    }
    if (smepayEnabled) {
      handlePayWithUPI();
    } else {
      saveOrderToSheet();
    }
  };

  if (cart.length === 0) {
    return null;
  }

  return (
    <div className="container py-4">
      <h1 className="text-center mb-4">Checkout</h1>

      <div className="row g-4 justify-content-center">
        <div className="col-lg-7">
          <div className="card shadow-sm p-4 mb-3">
            <h4 className="mb-3">Delivery details</h4>
            <p className="small text-muted mb-3">
              Confirm or update your details before saving the order.
            </p>
            {sessionEmail && (
              <p className="small text-muted mb-3">
                Signed in as <strong>{sessionEmail}</strong>
                {hasSuggestions
                  ? " — suggestions from past orders below."
                  : " — past order details applied when only one value was on file."}
              </p>
            )}

            <datalist id="co-dl-name">
              {hints.names.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
            <datalist id="co-dl-phone">
              {hints.phones.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
            <datalist id="co-dl-pin">
              {hints.pincodes.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>

            <div className="mb-3">
              <label className="form-label small fw-semibold" htmlFor="co-name">
                Full name
              </label>
              <input
                id="co-name"
                type="text"
                className={`form-control form-control-sm ${fieldErrors.name ? "is-invalid" : ""}`}
                autoComplete="name"
                list={hints.names.length > 0 ? "co-dl-name" : undefined}
                value={customer.name}
                onChange={(e) => onCustomerChange("name", e.target.value)}
                placeholder="Your name"
              />
              {fieldErrors.name && (
                <div className="invalid-feedback d-block">{fieldErrors.name}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label small fw-semibold" htmlFor="co-email">
                Email
              </label>
              <input
                id="co-email"
                type="email"
                className={`form-control form-control-sm ${fieldErrors.email ? "is-invalid" : ""}`}
                autoComplete="email"
                value={customer.email}
                onChange={(e) => onCustomerChange("email", e.target.value)}
                placeholder="you@example.com"
              />
              {fieldErrors.email && (
                <div className="invalid-feedback d-block">{fieldErrors.email}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label small fw-semibold" htmlFor="co-phone">
                Phone
              </label>
              <input
                id="co-phone"
                type="tel"
                className={`form-control form-control-sm ${fieldErrors.phone ? "is-invalid" : ""}`}
                autoComplete="tel"
                list={hints.phones.length > 0 ? "co-dl-phone" : undefined}
                value={customer.phone}
                onChange={(e) => onCustomerChange("phone", e.target.value)}
                placeholder="10-digit mobile"
              />
              {fieldErrors.phone && (
                <div className="invalid-feedback d-block">{fieldErrors.phone}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label small fw-semibold" htmlFor="co-address">
                Address
              </label>
              <textarea
                id="co-address"
                className={`form-control form-control-sm ${fieldErrors.address ? "is-invalid" : ""}`}
                autoComplete="street-address"
                rows={3}
                value={customer.address}
                onChange={(e) => onCustomerChange("address", e.target.value)}
                placeholder="House / street, area, city, state"
              />
              {hints.addresses.length > 1 && (
                <div className="mt-2">
                  <span className="small text-muted d-block mb-1">Previous addresses:</span>
                  <div className="d-flex flex-wrap gap-1">
                    {hints.addresses.map((a) => (
                      <button
                        key={a}
                        type="button"
                        className="btn btn-outline-secondary btn-sm text-start"
                        style={{ maxWidth: "100%", whiteSpace: "normal", fontSize: "0.75rem" }}
                        onClick={() => onCustomerChange("address", a)}
                      >
                        <BtnContent icon={IoLocationOutline} iconSize={14}>
                          {a.length > 80 ? `${a.slice(0, 80)}…` : a}
                        </BtnContent>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {fieldErrors.address && (
                <div className="invalid-feedback d-block">{fieldErrors.address}</div>
              )}
            </div>

            <div className="mb-0">
              <label className="form-label small fw-semibold" htmlFor="co-pin">
                Pincode
              </label>
              <input
                id="co-pin"
                type="text"
                inputMode="numeric"
                maxLength={6}
                className={`form-control form-control-sm ${fieldErrors.pincode ? "is-invalid" : ""}`}
                autoComplete="postal-code"
                list={hints.pincodes.length > 0 ? "co-dl-pin" : undefined}
                value={customer.pincode}
                onChange={(e) =>
                  onCustomerChange("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="6 digits"
              />
              {fieldErrors.pincode && (
                <div className="invalid-feedback d-block">{fieldErrors.pincode}</div>
              )}
            </div>

            <div className="mt-4 pt-3 border-top">
              <h5 className="mb-2">Shipping</h5>
              <p className="small text-muted mb-3">
                Choose a courier for delivery to your pincode. Rates use total cart weight and
                collapsed packaging (18 × 12 × 1.5 in per mailer, up to 5 boxes each).
              </p>
              {!/^\d{6}$/.test(pincode) ? (
                <p className="small text-muted mb-0">Enter a 6-digit pincode above to load options.</p>
              ) : ratesLoading ? (
                <p className="small text-muted mb-0">Loading shipping options…</p>
              ) : ratesError ? (
                <div className="alert alert-danger small py-2 mb-0" role="alert">
                  {ratesError}
                </div>
              ) : shippingOptions.length === 0 ? (
                <p className="small text-danger mb-0">
                  No shipping options are available for this pincode.
                </p>
              ) : (
                <div className="d-grid gap-2">
                  {ratesMeta && (
                    <p className="small text-muted mb-1">
                      From {ratesMeta.senderPincode} → {ratesMeta.receiverPincode}
                    </p>
                  )}
                  {shippingOptions.map((option) => (
                    <label
                      key={option.courierCompanyId}
                      className={`d-flex justify-content-between align-items-start gap-2 p-2 border rounded ${
                        selectedShipping?.courierCompanyId === option.courierCompanyId
                          ? "border-primary bg-light"
                          : ""
                      }`}
                      style={{ cursor: "pointer" }}
                    >
                      <span className="d-flex gap-2">
                        <input
                          type="radio"
                          name="checkout-shipping"
                          checked={selectedShipping?.courierCompanyId === option.courierCompanyId}
                          onChange={() => setSelectedShipping(option)}
                        />
                        <span>
                          <span className="fw-semibold d-block">{option.courierName}</span>
                          {option.etd ? (
                            <span className="small text-muted">Est. {option.etd}</span>
                          ) : null}
                        </span>
                      </span>
                      <span className="fw-semibold price-figure">₹{formatInrAmount(option.rate)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card shadow-sm p-4">
            <h4 className="mb-3">Order summary</h4>

            <ul className="list-unstyled small mb-3">
              {cart.map((item) => (
                <li key={item.id} className="py-1 border-bottom border-light">
                  <div className="d-flex justify-content-between">
                    <span>
                      {item.name} × {item.quantity}
                    </span>
                    <span className="price-figure">₹{formatInrAmount(item.price * item.quantity)}</span>
                  </div>
                  {item.giftLidMessage ? (
                    <p className="small text-muted fst-italic mb-0 mt-1">
                      Gift box: {item.giftLidMessage}
                      {item.giftLidLayout ? (
                        <span className="d-block fst-normal small mt-1" style={{ fontSize: "0.72rem" }}>
                          Print position: {giftLidPositionLabel(item.giftLidLayout)}
                          {" · text "}
                          {Number(item.giftLidLayout.scale).toFixed(2)}×
                          {item.giftLidTextColor ? ` · ${item.giftLidTextColor}` : ""}
                        </span>
                      ) : item.giftLidTextColor ? (
                        <span className="d-block fst-normal small mt-1" style={{ fontSize: "0.72rem" }}>
                          Text colour: {item.giftLidTextColor}
                        </span>
                      ) : null}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>

            {voucherEligible && sessionMatchesEmail(customer.email) && (
              <div className="form-check mb-3 p-3 border rounded" style={{ borderColor: "var(--gold, #b89b5e)" }}>
                <input
                  id="checkout-loyalty-voucher"
                  type="checkbox"
                  className="form-check-input"
                  checked={useLoyaltyVoucher}
                  onChange={(e) => setUseLoyaltyVoucher(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="checkout-loyalty-voucher">
                  Apply my <strong>₹{formatInrAmount(LOYALTY_VOUCHER_INR)} loyalty voucher</strong> (every
                  6th order). Logged in with this email — discount applies to your item total (up to ₹
                  {formatInrAmount(LOYALTY_VOUCHER_INR)}).
                </label>
              </div>
            )}

            {useLoyaltyVoucher && voucherEligible && voucherDiscount > 0 && (
              <div className="d-flex justify-content-between small text-success mb-2">
                <span>Loyalty voucher</span>
                <span>−₹{formatInrAmount(voucherDiscount)}</span>
              </div>
            )}

            <div className="mb-3 p-3 border rounded bg-light">
              <label className="form-label small fw-semibold mb-2" htmlFor="co-coupon">
                Coupon code
              </label>
              <div className="d-flex flex-wrap gap-2 align-items-stretch">
                <input
                  id="co-coupon"
                  type="text"
                  className="form-control form-control-sm flex-grow-1"
                  style={{ minWidth: "140px" }}
                  autoComplete="off"
                  value={couponInput}
                  onChange={(e) => {
                    setCouponInput(e.target.value);
                    setCouponError(null);
                    setAppliedCoupon(null);
                  }}
                  placeholder="Enter code"
                  disabled={couponApplying}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleApplyCoupon}
                  disabled={couponApplying || !couponInput.trim()}
                >
                  <BtnContent icon={IoTicketOutline}>{couponApplying ? "Checking…" : "Apply"}</BtnContent>
                </button>
                {appliedCoupon && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleRemoveCoupon}
                  >
                    <BtnContent icon={IoCloseOutline}>Remove</BtnContent>
                  </button>
                )}
              </div>
              {couponError && (
                <p className="small text-danger mb-0 mt-2">{couponError}</p>
              )}
              {appliedCoupon && !couponError && (
                <p className="small text-success mb-0 mt-2">
                  Applied <strong>{appliedCoupon.code}</strong>
                </p>
              )}
            </div>

            {appliedCoupon && couponDiscount > 0 && (
              <div className="d-flex justify-content-between small text-success mb-2">
                <span>Coupon discount</span>
                <span>−₹{formatInrAmount(couponDiscount)}</span>
              </div>
            )}

            {/^\d{6}$/.test(pincode) && ratesError && !ratesLoading && (
              <div className="alert alert-warning small py-2 mb-3" role="alert">
                Shipping: {ratesError}
              </div>
            )}

            <div className="d-flex justify-content-between small mb-2">
              <span>Items subtotal</span>
              <span className="price-figure">₹{formatInrAmount(merchandiseTotal)}</span>
            </div>

            <div className="d-flex justify-content-between small mb-3">
              <span>
                Shipping
                {selectedShipping?.courierName ? ` (${selectedShipping.courierName})` : ""}
              </span>
              <span className="price-figure">
                {ratesLoading
                  ? "…"
                  : ratesError
                    ? "—"
                    : selectedShipping
                      ? `₹${formatInrAmount(shippingCost)}`
                      : "—"}
              </span>
            </div>

            <div className="d-flex justify-content-between align-items-baseline mb-3">
              <span className="fw-semibold">
                Total ({cart.reduce((s, i) => s + i.quantity, 0)} items)
              </span>
              <span className="price-figure price-figure--emphasis">
                ₹{formatInrAmount(orderPayableTotal)}
              </span>
            </div>

            <TrustBadges className="mb-4" />

            <div className="mb-4">
              <h5 className="mb-2">Payment method</h5>
              {smepayEnabled ? (
                <div className="d-grid gap-2">
                  <label
                    className={`d-flex align-items-start gap-2 p-2 border rounded ${
                      paymentMethod === "prepaid" ? "border-primary bg-light" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="radio"
                      name="checkout-payment"
                      className="mt-1"
                      checked={paymentMethod === "prepaid"}
                      onChange={() => setPaymentMethod("prepaid")}
                    />
                    <span>
                      <span className="fw-semibold d-block">Pay online with UPI</span>
                      <span className="small text-muted">
                        Secure checkout via SMEPay. Your order is saved after payment is confirmed.
                      </span>
                    </span>
                  </label>
                  <label
                    className={`d-flex align-items-start gap-2 p-2 border rounded ${
                      paymentMethod === "cod" ? "border-primary bg-light" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="radio"
                      name="checkout-payment"
                      className="mt-1"
                      checked={paymentMethod === "cod"}
                      onChange={() => setPaymentMethod("cod")}
                    />
                    <span>
                      <span className="fw-semibold d-block">Cash on delivery (COD)</span>
                      <span className="small text-muted">
                        Pay the courier in cash when your gift box arrives.
                      </span>
                    </span>
                  </label>
                </div>
              ) : (
                <p className="small text-muted mb-0">
                  Cash on delivery — pay in full when your order is delivered.
                </p>
              )}
            </div>

            {smepayEnabled &&
              !isCod &&
              smepaySession &&
              smepaySession.displayMode === "inline" &&
              smepaySession.qr_code && (
                <div className="text-center mb-3 p-3 border rounded bg-light">
                  <p className="small fw-semibold mb-2">Scan to pay with UPI</p>
                  <div className="d-inline-block p-2 bg-white rounded">
                    <QRCodeSVG value={smepaySession.qr_code} size={200} level="M" />
                  </div>
                </div>
              )}

            {smepayEnabled &&
              !isCod &&
              smepaySession &&
              smepaySession.displayMode === "inline" &&
              smepaySession.intents &&
              Object.keys(smepaySession.intents).length > 0 && (
                <div className="mb-3">
                  <p className="small fw-semibold mb-2">Pay with an app</p>
                  <div className="d-flex flex-wrap gap-2 justify-content-center">
                    {Object.entries(smepaySession.intents).map(([key, url]) => {
                      if (!url || typeof url !== "string") return null;
                      const icon = INTENT_ICONS[key.toLowerCase()];
                      return (
                        <a
                          key={key}
                          href={url}
                          className="btn btn-outline-dark btn-sm d-flex align-items-center gap-1"
                          rel="noopener noreferrer"
                        >
                          {icon ? (
                            <img src={icon} alt="" width={20} height={20} />
                          ) : null}
                          <span className="text-capitalize">{key}</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

            {smepayEnabled &&
              !isCod &&
              smepaySession &&
              smepaySession.displayMode === "inline" &&
              smepaySession.payment_link && (
                <div className="mb-3 text-center">
                  <a
                    href={smepaySession.payment_link}
                    className="btn btn-outline-primary btn-sm"
                    rel="noopener noreferrer"
                  >
                    <BtnContent icon={IoLinkOutline}>Open payment link</BtnContent>
                  </a>
                </div>
              )}

            {sheetMessage && (
              <p
                className={`small mb-3 ${
                  sheetMessage.startsWith("Order saved")
                    ? "text-success"
                    : sheetMessage.startsWith("Please complete")
                      ? "text-warning"
                      : sheetMessage.includes("No network request") ||
                          sheetMessage.startsWith("Could not") ||
                          sheetMessage.includes("Network error")
                        ? "text-danger"
                        : "text-muted"
                }`}
              >
                {sheetMessage}
              </p>
            )}

            <div className="checkout-action-buttons d-grid gap-2 mb-2">
              <button
                type="button"
                onClick={handlePrimaryClick}
                disabled={sheetSubmitting || ratesLoading || !selectedShipping}
                className="btn btn-primary py-2 fw-semibold checkout-action-primary"
              >
                {sheetSubmitting ? (
                  isCod ? (
                    "Placing order…"
                  ) : smepayEnabled ? (
                    "Starting payment…"
                  ) : (
                    "Saving order…"
                  )
                ) : isCod ? (
                  <BtnContent icon={IoCashOutline}>Place order (Cash on delivery)</BtnContent>
                ) : smepayEnabled ? (
                  <BtnContent icon={IoCardOutline}>Pay with UPI (SMEPay)</BtnContent>
                ) : (
                  <BtnContent icon={IoCheckmarkCircle}>Place order</BtnContent>
                )}
              </button>
              {smepayEnabled && !isCod && smepaySession && (
                <button
                  type="button"
                  onClick={handleCheckPayment}
                  disabled={sheetSubmitting}
                  className="btn btn-outline-primary py-2 fw-semibold"
                >
                  <BtnContent icon={IoRefreshOutline}>Check payment</BtnContent>
                </button>
              )}
              <Link
                to="/cart"
                className="btn btn-outline-secondary py-2 checkout-back-to-cart"
              >
                <BtnContent icon={IoArrowBackOutline}>Back to cart</BtnContent>
              </Link>
            </div>

            {isCod ? (
              <p className="small text-muted mb-0">
                Your order will be placed now. Please keep the exact amount ready to pay the courier
                at delivery.
              </p>
            ) : smepayEnabled ? (
              <p className="small text-muted mb-0">
                Secure UPI checkout via SMEPay. Your order is saved only after payment is confirmed.
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;
